import { useState } from "react";
import { X } from "lucide-react";
import PricingPlans from "@/components/PricingPlans";
import { apiClient } from "@/api/client";
import { theme } from "@/lib/theme";
import { cn } from "@/lib/utils";

interface ChangePlanModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlanKey: string | null;
  subscriptionId: string | null;
  onSuccess: () => void;
}

export default function ChangePlanModal({
  isOpen,
  onClose,
  currentPlanKey,
  subscriptionId,
  onSuccess,
}: ChangePlanModalProps) {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showConfirmation, setShowConfirmation] = useState(false);

  if (!isOpen) return null;

  const handlePlanSelect = (planKey: string) => {
    if (planKey === currentPlanKey) {
      setError("This is already your current plan.");
      return;
    }
    setSelectedPlan(planKey);
    setShowConfirmation(true);
    setError(null);
  };

  const handleConfirm = async () => {
    if (!selectedPlan) return;

    setIsLoading(true);
    setError(null);

    try {
      await apiClient.changePlan(selectedPlan, subscriptionId || undefined);
      onSuccess();
      onClose();
      setSelectedPlan(null);
      setShowConfirmation(false);
    } catch (err) {
      console.error("Failed to change plan", err);
      setError(err instanceof Error ? err.message : "Failed to change plan. Please try again.");
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedPlan(null);
    setShowConfirmation(false);
    setError(null);
  };

  const getPlanDisplayName = (planKey: string | null): string => {
    if (!planKey) return "Unknown";
    const names: Record<string, string> = {
      usage_only_monthly: "Usage Only",
      basic_monthly: "Basic Monthly",
      basic_yearly: "Basic Yearly",
      premium_monthly: "Premium Monthly",
      premium_yearly: "Premium Yearly",
    };
    return names[planKey] || planKey.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase());
  };

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pt-20 md:pt-4 bg-black/50 backdrop-blur-sm">
      <div
        className={cn(
          "relative w-full max-w-4xl max-h-[calc(100vh-6rem)] md:max-h-[90vh] overflow-y-auto",
          theme.cards.base,
          "p-6 md:p-8",
        )}
      >
        <button
          onClick={onClose}
          disabled={isLoading}
          className="absolute top-4 right-4 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-gray-900 dark:text-white" />
        </button>

        <h2 className="text-2xl font-black text-gray-900 dark:text-white mb-6">Change Plan</h2>

        {error && (
          <div className={cn("mb-6 p-4 rounded-lg", theme.borders.error, theme.colors.errorBg, theme.text.error)}>
            {error}
          </div>
        )}

        {showConfirmation && selectedPlan ? (
          <div className="space-y-6">
            <div>
              <p className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Confirm Plan Change</p>
              <div className="space-y-2">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  You're changing from: <span className="font-semibold">{getPlanDisplayName(currentPlanKey)}</span>
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  To: <span className="font-semibold">{getPlanDisplayName(selectedPlan)}</span>
                </p>
              </div>
            </div>

            <div className={cn("p-4 rounded-lg", theme.borders.neubrutalism, "bg-yellow-50 dark:bg-yellow-900/20")}>
              <p className="text-sm text-gray-900 dark:text-white font-medium">
                ⚠️ This change takes effect immediately. No proration will be applied.
              </p>
            </div>

            <div className="flex gap-4">
              <button
                onClick={handleCancel}
                disabled={isLoading}
                className={cn(theme.buttons.secondary, "flex-1 px-6 py-3 rounded-xl", isLoading && "opacity-50 cursor-not-allowed")}
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={isLoading}
                className={cn(theme.buttons.primary, "flex-1 px-6 py-3 rounded-xl", isLoading && "opacity-50 cursor-not-allowed")}
              >
                {isLoading ? "Changing Plan..." : "Confirm Change"}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">Select a new plan:</p>
            <PricingPlans 
              onSelectPlan={handlePlanSelect} 
              showCta={true} 
              ctaText="Select Plan"
              currentPlanKey={currentPlanKey}
              hideMarketing={true}
            />
          </div>
        )}
      </div>
    </div>
  );
}

